export interface WorkingHour {
  openTime?: string;
  closeTime?: string;
  operationDate?: string;
}

export interface StoreSchedule {
  workingHours?: WorkingHour[] | null;
  // Curbside hours are included in the API response but are intentionally unused by the store closed check
  curbsideHours?: WorkingHour[] | null;
}

const getLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Checks whether a formatted hours string indicates the store is open.
 * Returns false if hours are null, undefined, empty, or represent a closed status (e.g. 'Closed', 'CLOSED').
 * 'CLOSED - CLOSED' is retained defensively for legacy or raw unnormalized string formats.
 */
export function isStoreOpen(hours: string | null | undefined): boolean {
  if (!hours) return false;
  const normalized = hours.trim().toUpperCase();
  // 'CLOSED - CLOSED' is maintained defensively for legacy or raw unnormalized formatted inputs
  return normalized !== 'CLOSED' && normalized !== 'CLOSED - CLOSED';
}

/**
 * Formats a working hour entry into a display string or 'Closed'.
 */
const formatHours = (match?: WorkingHour | null): string => {
  if (match && match.openTime && match.closeTime) {
    const open = match.openTime.trim();
    const close = match.closeTime.trim();
    if (open.toUpperCase() === 'CLOSED' || close.toUpperCase() === 'CLOSED') {
      return 'Closed';
    }
    return `${open} - ${close}`;
  }
  return 'Closed';
};

/**
 * Parses a list of hours (such as workingHours or curbsideHours) for today and tomorrow.
 */
export function parseHoursList(hours: WorkingHour[] | null | undefined): { today: string; tomorrow: string } {
  if (!hours || hours.length === 0) {
    return { today: 'Closed', tomorrow: 'Closed' };
  }

  const todayDate = new Date();
  const tomorrowDate = new Date();
  tomorrowDate.setDate(todayDate.getDate() + 1);

  const todayStr = getLocalDateString(todayDate);
  const tomorrowStr = getLocalDateString(tomorrowDate);

  const todayMatch = hours.find((h) => h?.operationDate === todayStr);
  const tomorrowMatch = hours.find((h) => h?.operationDate === tomorrowStr);

  return {
    today: formatHours(todayMatch),
    tomorrow: formatHours(tomorrowMatch),
  };
}

/**
 * Parses a store's schedule and returns the formatted display hours for today and tomorrow.
 * Safely handles missing/null schedule or workingHours/curbsideHours.
 *
 * @param schedule The store's schedule containing working hours.
 * @returns An object detailing the operational status/hours for today and tomorrow.
 */
export function parseStoreHours(schedule: StoreSchedule | null | undefined): { today: string; tomorrow: string } {
  return parseHoursList(schedule?.workingHours);
}
